// ResilienceScreen — what shows when a week goes wrong. Never a red X, never
// 'Game Over'. A calm breakdown of what happened, each mentor's reaction in
// character, and a concrete recovery plan. A setback is a lesson.

import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import type { MentorId, Txn } from '@biz-kid/engine';
import { useGameState } from '@biz-kid/store';
import { Card, Eyebrow, Button, money } from '../ui/kit.js';
import { useLife } from './store.js';
import { RESILIENCE_LINES, lossBreakdown, recoveryPlan } from './resilience.js';
import { useVoice } from '../voice/VoiceContext.js';

export type ResilienceEvent =
  | { type: 'lossWeek'; week: number }
  | { type: 'freedomDrop'; from: number; to: number };

const sumWeek = (ledger: Txn[], week: number, type: Txn['type']) =>
  ledger.filter((t) => t.week === week && t.type === type).reduce((s, t) => s + Math.abs(t.amount), 0);

export function ResilienceScreen({ event, onClose }: { event: ResilienceEvent; onClose: () => void }) {
  const ledger = useGameState((s) => s.ledger);
  const happiness = useGameState((s) => s.profile.happiness);
  const life = useLife();

  // MIKO SPEAKS — the setback moment is where a warm voice matters most.
  // Calm and encouraging, once per mount; never an alarm.
  const { speak } = useVoice();
  const spokeRef = useRef(false);
  useEffect(() => {
    if (spokeRef.current) return;
    spokeRef.current = true;
    void speak("Rough week — it happens to every boss. Let's look at what happened, and bounce back.");
  }, [speak]);

  const week = event.type === 'lossWeek' ? event.week : 0;
  const breakdown = event.type === 'lossWeek' ? lossBreakdown(ledger, week) : null;
  const plan = recoveryPlan({
    happiness,
    marketing: sumWeek(ledger, week, 'marketing'),
    loanPayment: sumWeek(ledger, week, 'loan_payment'),
    investorPayout: sumWeek(ledger, week, 'investor_payout'),
    idleCash: life.cash,
  });
  const mentors = life.mastermind;

  return (
    <div className="bk-app" style={{ position: 'fixed', inset: 0, zIndex: 80, overflowY: 'auto' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: 'clamp(16px, 4vw, 28px)', paddingBottom: 80 }}>
        <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 44 }}>🌧️</div>
          <Eyebrow color="var(--warn)">ROUGH WEEK. IT HAPPENS.</Eyebrow>
          <h1 style={{ margin: '4px 0 0', fontSize: 26, fontWeight: 900, color: 'var(--text)' }}>WHAT HAPPENED</h1>
          <p style={{ margin: '6px auto 0', maxWidth: 380, fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5, fontWeight: 700 }}>
            Every boss has off weeks. Let’s bounce back.
          </p>
        </motion.div>

        {/* the breakdown */}
        <Card style={{ marginBottom: 14 }}>
          <Eyebrow>THE BREAKDOWN</Eyebrow>
          {breakdown ? (
            <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
              <Row label="Money you made (sales)" value={breakdown.moneyIn} tone="up" />
              {breakdown.out.map((r) => (
                <Row key={r.label} label={r.label} value={-r.amount} tone="down" />
              ))}
              <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
              <Row label={`Week ${week} result`} value={breakdown.net} tone="down" bold />
              <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>{breakdown.why}</p>
            </div>
          ) : event.type === 'freedomDrop' ? (
            <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5 }}>
              Your FREEDOM LEVEL dipped from <b>{event.from}%</b> to <b style={{ color: 'var(--down)' }}>{event.to}%</b>. Something costs
              more than your assets bring in — let’s find it and fix it.
            </p>
          ) : null}
        </Card>

        {/* mentors */}
        {mentors.length > 0 && (
          <Card style={{ marginBottom: 14 }}>
            <Eyebrow color="var(--brand)">YOUR CREW’S GOT YOU</Eyebrow>
            <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
              {mentors.map((m) => (
                <div key={m.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 26, flexShrink: 0 }}>{m.icon}</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text)' }}>{m.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5, fontStyle: 'italic' }}>
                      “{RESILIENCE_LINES[m.id as MentorId] ?? 'Dust yourself off and go again.'}”
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* recovery plan */}
        <Card style={{ marginBottom: 18 }}>
          <Eyebrow color="var(--up)">YOUR COMEBACK · THIS WEEK</Eyebrow>
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            {plan.map((a, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: 10, borderRadius: 12, background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                <span style={{ fontSize: 22 }}>{a.icon}</span>
                <span style={{ fontSize: 13.5, color: 'var(--text)', lineHeight: 1.45 }}>{a.text}</span>
              </div>
            ))}
          </div>
          <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-3)' }}>
            💡 Recover a loss week back to a profit and you’ll earn <b>+5 Persistence</b> — bouncing back is a skill.
          </p>
        </Card>

        <div style={{ textAlign: 'center' }}>
          <Button tone="primary" size="lg" onClick={onClose}>I GOT THIS →</Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, tone, bold }: { label: string; value: number; tone: 'up' | 'down'; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{ fontSize: bold ? 15 : 13.5, fontWeight: bold ? 800 : 600, color: 'var(--text)' }}>{label}</span>
      <span className="bk-tnum" style={{ fontWeight: bold ? 900 : 800, fontSize: bold ? 17 : 14, color: value >= 0 ? 'var(--up)' : 'var(--down)' }}>
        {value >= 0 ? '+' : '−'}{money(Math.abs(value))}
      </span>
    </div>
  );
}
